const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Token management
function getToken() { return localStorage.getItem('auth_token'); }
function setToken(token) { localStorage.setItem('auth_token', token); }
function clearToken() { localStorage.removeItem('auth_token'); }

async function request(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const config = { ...options, headers };
  const response = await fetch(url, config);
  
  // If 401, clear token and redirect to login
  if (response.status === 401) {
    clearToken();
    window.dispatchEvent(new Event('auth_expired'));
    throw new Error('Session expired');
  }
  
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
  return data;
}

// Auth
export const login = async (username, password) => {
  const url = `${API_BASE}/auth/login`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Login failed');
  if (data.success && data.token) setToken(data.token);
  return data;
};
export const verifyToken = async () => {
  const token = getToken();
  if (!token) return false;
  try {
    const url = `${API_BASE}/auth/verify`;
    const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
    const data = await response.json();
    return data.success === true;
  } catch { return false; }
};
export const logout = () => { clearToken(); };
export { getToken };

// Home / Dashboard
export const getHomeStats = () => request('/home/stats');
export const getHomeCalendar = () => request('/home/calendar');
export const getHomeJobs = () => request('/home/jobs');

// Gateways
export const getGateways = () => request('/gateways');
export const addGateway = (data) => request('/gateways', { method: 'POST', body: JSON.stringify(data) });
export const toggleGateway = (id) => request(`/gateways/${id}/toggle`, { method: 'PUT' });
export const deleteGateway = (id) => request(`/gateways/${id}`, { method: 'DELETE' });
export const checkGatewayCap = (id) => request(`/gateways/${id}/check-cap`, { method: 'POST' });

// Rebill (Manual Jobs)
export const getRebillStatus = () => request('/rebill/status');
export const createRebillJob = (data) => request('/rebill/jobs', { method: 'POST', body: JSON.stringify(data) });
export const startRebillJob = (id) => request(`/rebill/jobs/${id}/start`, { method: 'POST' });
export const stopRebillJob = (id) => request(`/rebill/jobs/${id}/stop`, { method: 'POST' });
export const resumeRebillJob = (id) => request(`/rebill/jobs/${id}/resume`, { method: 'POST' });
export const getRebillJobDetails = (id) => request(`/rebill/jobs/${id}/details`);
export const getRebillJobs = () => request('/rebill/jobs');
export const getAvailableClients = (params = '') => request(`/rebill/available-clients${params ? '?' + params : ''}`);
export const getPendingClients = (params = '') => request(`/rebill/pending${params ? '?' + params : ''}`);
export const getSuccessTransactions = (params = '') => request(`/rebill/transactions/success${params ? '?' + params : ''}`);
export const getDeclinedTransactions = (params = '') => request(`/rebill/transactions/declined${params ? '?' + params : ''}`);

// AutoRb (Automatic 72h)
export const getAutoRbStatus = () => request('/autorb/status');
export const startAutoRb = (gatewayIds = []) => request('/autorb/start', { method: 'POST', body: JSON.stringify({ gateway_ids: gatewayIds }) });
export const stopAutoRb = () => request('/autorb/stop', { method: 'POST' });
export const planAutoRb = () => request('/autorb/plan', { method: 'POST' });
export const getAutoRbScheduled = (params = '') => request(`/autorb/scheduled${params ? '?' + params : ''}`);

// Clients
export const getClients = (params = '') => request(`/clients${params ? '?' + params : ''}`);
export const getDeclinedClients = (params = '') => request(`/clients/declined${params ? '?' + params : ''}`);
export const getClientStats = () => request('/clients/stats');
export const syncExternalClients = () => request('/clients/sync', { method: 'POST' });
export const getExternalDbInfo = () => request('/clients/external-info');
export const getClientTriplets = (id) => request(`/clients/${id}/triplets`);

// Recycle Billing
export const getRecycleEntries = (params = '') => request(`/recycle${params ? '?' + params : ''}`);
export const getRecycleStats = () => request('/recycle/stats');
export const getRecycleJobs = () => request('/recycle/jobs');
export const createRecycleJob = (data) => request('/recycle/jobs', { method: 'POST', body: JSON.stringify(data) });
export const startRecycleJob = (id) => request(`/recycle/jobs/${id}/start`, { method: 'POST' });
export const stopRecycleJob = (id) => request(`/recycle/jobs/${id}/stop`, { method: 'POST' });
export const reassignDeactivatedGateways = () => request('/recycle/reassign-deactivated', { method: 'POST' });
export const getRecycleSettings = () => request('/recycle/settings');
export const updateRecycleSettings = (data) => request('/recycle/settings', { method: 'PUT', body: JSON.stringify(data) });

// Reports
export const getReports = (params = '') => request(`/reports${params ? '?' + params : ''}`);
export const getReportStats = (params = '') => request(`/reports/stats${params ? '?' + params : ''}`);
export const getClientTransactions = (id) => request(`/reports/client/${id}`);
export const refundTransaction = (transactionId) => request('/reports/refund', { method: 'POST', body: JSON.stringify({ transaction_id: transactionId }) });

// Friendlies USA Settings
export const getFriendliesSettings = () => request('/friendlies/settings');
export const updateFriendliesSettings = (data) => request('/friendlies/settings', { method: 'PUT', body: JSON.stringify(data) });

// Blacklist (CB360)
export const getBlacklist = (params = '') => request(`/blacklist${params ? '?' + params : ''}`);
export const getBlacklistStats = () => request('/blacklist/stats');
export const syncBlacklist = () => request('/blacklist/sync', { method: 'POST' });
export const importBlacklistCsv = (csvData) => request('/blacklist/import-csv', { method: 'POST', body: JSON.stringify({ csvData }) });
export const getEmailBlacklistStats = () => request('/blacklist/email-stats');
